import { useEffect, useState } from 'react';
import { Box, Chip, Tooltip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useMaterialUIController } from 'context';
import { getCrmColors } from '@layouts/pages/CRM/kanban-board/crm-theme';
import { WhatsappTemplateService } from '@layouts/pages/Settings/whatsapp-templates/services/whatsapp-template.service';
import { LeadVariableCategory } from '@layouts/pages/Settings/whatsapp-templates/services/responses/whatsapp-template.response';

/// O catálogo só muda com a publicação do backend: guardar no módulo evita uma chamada por campo.
let cache: LeadVariableCategory[] | null = null;
let pending: Promise<LeadVariableCategory[]> | null = null;

export const loadVariables = (): Promise<LeadVariableCategory[]> => {
    if (cache) return Promise.resolve(cache);

    pending ??= new WhatsappTemplateService()
        .getVariables()
        .then((res) => {
            cache = res.data.data ?? [];
            return cache;
        })
        .catch(() => []);

    return pending;
};

interface Props {
    onInsert: (key: string) => void;
    /// large nas telas de configuração, onde o campo é o assunto da tela.
    size?: 'small' | 'large';
}

/// Botões de variável: oferecem só o que o servidor sabe preencher, senão a chave sairia literal.
export const VariableChips = ({ onInsert, size = 'small' }: Props) => {
    const { t } = useTranslation();
    const [controller] = useMaterialUIController();
    const cc = getCrmColors(controller.darkMode);

    const [categories, setCategories] = useState<LeadVariableCategory[]>([]);

    useEffect(() => {
        let alive = true;

        loadVariables().then((loaded) => {
            if (alive) setCategories(loaded);
        });

        return () => { alive = false; };
    }, []);

    if (categories.length === 0) return null;

    const large = size === 'large';

    return (
        <Box sx={{ mt: large ? 1.5 : 0.75 }}>
            <Typography sx={{ fontSize: large ? 13 : 11, color: cc.textMuted, mb: large ? 1 : 0.5 }}>
                {t('automacoes.cliqueParaInserir')}
            </Typography>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: large ? 1 : 0.6 }}>
                {categories.flatMap((category) => category.variables).map((variable) => (
                    <Tooltip
                        key={variable.key}
                        title={`${variable.label} — ${t('automacoes.exemplo')}: ${variable.example}`}
                    >
                        <Chip
                            label={variable.label}
                            size="small"
                            onClick={() => onInsert(variable.key)}
                            sx={{
                                cursor: 'pointer',
                                fontSize: large ? 13.5 : 11,
                                height: large ? 32 : undefined,
                                px: large ? 0.75 : 0,
                                borderRadius: large ? '16px' : undefined,
                                fontWeight: 600,
                                bgcolor: cc.countBg,
                                color: cc.textSecondary,
                                border: `1px solid ${cc.border}`,
                                '&:hover': {
                                    bgcolor: cc.accentSoftBg,
                                    color: cc.onTint,
                                    borderColor: cc.accent,
                                },
                            }}
                        />
                    </Tooltip>
                ))}
            </Box>
        </Box>
    );
};

/// Insere a marcação na posição do cursor e devolve o texto novo (sem a posição, iria sempre ao fim).
export const insertAtCursor = (
    field: HTMLTextAreaElement | HTMLInputElement | null,
    value: string,
    key: string
): string => {
    const token = `{{${key}}}`;

    if (!field) return value + token;

    const start = field.selectionStart ?? value.length;
    const end = field.selectionEnd ?? start;
    const next = value.slice(0, start) + token + value.slice(end);

    requestAnimationFrame(() => {
        field.focus();
        const at = start + token.length;
        field.setSelectionRange(at, at);
    });

    return next;
};
